import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Copy, Users, DoorOpen, MoreVertical, UserMinus, Shield, ShieldOff } from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../lib/AuthContext";
import { householdsApi, formatError } from "../lib/api";
import { toast } from "sonner";
import { PrivacyDefaultCard } from "../components/PrivacyDefaultCard";
import { PersonalStoringCard } from "../components/PersonalStoringCard";
import { MultiHouseholdPanel } from "../components/MultiHouseholdPanel";
import { LeaveHouseholdDialog } from "../components/LeaveHouseholdDialog";
import { MemberAvatar } from "../components/FamilyAvatarBubbles";
import { HouseholdIconPicker } from "../components/HouseholdIconPicker";

const Household = () => {
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const [household, setHousehold] = useState(null);
  const [leaveOpen, setLeaveOpen] = useState(false);
  const householdId = user?.household_id;

  const loadHousehold = useCallback(async () => {
    if (!householdId) {
      setHousehold(null);
      return;
    }
    try {
      const h = await householdsApi.me();
      setHousehold(h);
    } catch (err) {
      console.error("Failed to load household:", err);
      setHousehold(null);
    }
  }, [householdId]);

  useEffect(() => { loadHousehold(); }, [loadHousehold]);

  const copyCode = () => {
    if (!household) return;
    navigator.clipboard?.writeText(household.join_code);
    toast.success("Join code copied");
  };

  const leaveHousehold = () => {
    // Open the rich confirmation dialog instead of window.confirm() — it
    // shows accurate container/location counts and a louder warning when
    // the user is the last member (and thus about to delete everything).
    setLeaveOpen(true);
  };

  const onLeaveConfirmed = async (result) => {
    await refresh();
    // If the user still belongs to another household, the backend auto-
    // switched to it — stay on this page so they see the new active one.
    // Otherwise route them through onboarding.
    if (!result?.next_active) navigate("/onboarding");
  };

  const onMembershipsChanged = useCallback(async () => {
    await refresh();
    await loadHousehold();
  }, [refresh, loadHousehold]);

  return (
    <div className="px-5 pt-6 pb-8">
      <button
        onClick={() => navigate("/profile")}
        data-testid="household-back-btn"
        className="text-sm text-[#4F5D75] hover:text-[#2D3142] flex items-center gap-2 mb-4"
      >
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
      >
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#8A93A6]">
          Household
        </div>
        <div className="mt-1 flex items-center gap-3">
          {household && (
            <HouseholdIconPicker
              household={household}
              currentUserId={user?.id}
              onChanged={(updated) => setHousehold(updated)}
            />
          )}
          <h1 className="font-heading text-3xl font-bold text-[#2D3142] min-w-0 truncate">
            {household ? household.name : "Your household"}
          </h1>
        </div>
      </motion.div>

      {/* Multi-household controls — switcher + create/join CTAs. Renders for
          every signed-in user so free users see the upsell, and premium
          users get the full create/join/switch flow. */}
      <MultiHouseholdPanel
        user={user}
        activeHousehold={household}
        onChanged={onMembershipsChanged}
      />

      {household ? (
        <div className="mt-6 tup-card p-5" data-testid="household-detail-card">
          <button
            onClick={copyCode}
            data-testid="copy-code-btn"
            className="w-full flex items-center justify-between bg-[#F5F3ED] hover:bg-[#EBE7DA] rounded-xl px-4 py-3 transition-colors"
          >
            <div className="text-left">
              <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#8A93A6]">Join code</div>
              <div className="font-mono text-lg tracking-[0.3em] text-[#2D3142] mt-0.5">{household.join_code}</div>
            </div>
            <Copy className="w-4 h-4 text-[#277A82]" />
          </button>

          <div className="mt-5">
            <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#8A93A6] mb-2 flex items-center gap-1.5">
              <Users className="w-3.5 h-3.5" /> Members ({household.members.length})
            </div>
            <ul className="divide-y divide-[#E2E0D8]" data-testid="household-members">
              {household.members.map((m) => (
                <MemberRow
                  key={m.id}
                  member={m}
                  currentUserId={user?.id}
                  ownerCount={household.members.filter((x) => x.is_owner).length}
                  iAmOwner={household.members.find((x) => x.id === user?.id)?.is_owner}
                  onChanged={(updated) => setHousehold(updated)}
                />
              ))}
            </ul>
          </div>

          <Button
            onClick={leaveHousehold}
            data-testid="leave-household-btn"
            variant="outline"
            className="w-full mt-5 border-[#E07A5F] text-[#E07A5F] hover:bg-[#FBEAE5]"
          >
            <DoorOpen className="w-4 h-4 mr-2" /> Leave household
          </Button>
        </div>
      ) : (
        <div className="mt-6 tup-card p-5">
          <p className="text-sm text-[#4F5D75]">You're not in a household yet.</p>
          <Button
            onClick={() => navigate("/onboarding")}
            className="w-full mt-3 bg-[#277A82] hover:bg-[#1F5566] text-white"
          >
            Set up a household
          </Button>
        </div>
      )}

      <PrivacyDefaultCard />
      <PersonalStoringCard />

      <LeaveHouseholdDialog
        open={leaveOpen}
        onClose={() => setLeaveOpen(false)}
        householdName={household?.name || "your household"}
        onConfirmed={onLeaveConfirmed}
      />
    </div>
  );
};

const MemberRow = ({ member, currentUserId, ownerCount, iAmOwner, onChanged }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const isSelf = member.id === currentUserId;
  const canManage = iAmOwner && !isSelf;
  const wouldOrphanOwner = member.is_owner && ownerCount === 1;

  const act = async (fn, successMsg) => {
    setBusy(true);
    setMenuOpen(false);
    try {
      const updated = await fn();
      toast.success(successMsg);
      onChanged?.(updated);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  const promote = () =>
    act(() => householdsApi.setMemberRole(member.id, "owner"), `${member.name} is now an owner`);
  const demote = () =>
    act(() => householdsApi.setMemberRole(member.id, "member"), `${member.name} is no longer an owner`);
  const removeFromHousehold = () => {
    if (!confirm(`Remove ${member.name} from this household?`)) return;
    return act(() => householdsApi.removeMember(member.id), `${member.name} removed`);
  };

  return (
    <li
      className="flex items-center justify-between py-3 relative"
      data-testid={`member-row-${member.id}`}
    >
      <div className="flex items-center gap-3 min-w-0">
        <MemberAvatar
          member={member}
          size={36}
          border={false}
          testid={`member-avatar-${member.id}`}
        />
        <div className="min-w-0">
          <div className="font-semibold text-[#2D3142] text-sm truncate">
            {member.name} {isSelf && <span className="text-[#8A93A6] font-normal">(you)</span>}
          </div>
          <div className="text-xs text-[#8A93A6] truncate">{member.email}</div>
        </div>
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        {member.is_owner && (
          <span
            className="text-[10px] uppercase tracking-wider font-bold text-[#277A82] bg-[#DCEDF0] px-2 py-1 rounded-full"
            data-testid={`member-owner-pill-${member.id}`}
          >
            Owner
          </span>
        )}
        {canManage && (
          <div className="relative">
            <button
              onClick={() => setMenuOpen((v) => !v)}
              data-testid={`member-menu-btn-${member.id}`}
              disabled={busy}
              aria-label="Member actions"
              className="w-8 h-8 rounded-full hover:bg-[#F5F3ED] flex items-center justify-center text-[#4F5D75]"
            >
              <MoreVertical className="w-4 h-4" />
            </button>
            {menuOpen && (
              <>
                <button
                  className="fixed inset-0 z-30"
                  onClick={() => setMenuOpen(false)}
                  aria-label="Close menu"
                  tabIndex={-1}
                />
                <div
                  className="absolute right-0 top-9 z-40 w-56 bg-white border border-[#E2E0D8] rounded-xl shadow-lg overflow-hidden"
                  data-testid={`member-menu-${member.id}`}
                >
                  {member.is_owner ? (
                    <button
                      onClick={demote}
                      disabled={wouldOrphanOwner}
                      data-testid={`member-demote-${member.id}`}
                      className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-left hover:bg-[#F5F3ED] disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <ShieldOff className="w-4 h-4 text-[#4F5D75]" />
                      <span>Remove owner role</span>
                    </button>
                  ) : (
                    <button
                      onClick={promote}
                      data-testid={`member-promote-${member.id}`}
                      className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-left hover:bg-[#F5F3ED]"
                    >
                      <Shield className="w-4 h-4 text-[#277A82]" />
                      <span>Make owner</span>
                    </button>
                  )}
                  <button
                    onClick={removeFromHousehold}
                    disabled={wouldOrphanOwner}
                    data-testid={`member-remove-${member.id}`}
                    className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-left text-[#9C3E1D] hover:bg-[#FBEAE5] disabled:opacity-40 disabled:cursor-not-allowed border-t border-[#E2E0D8]"
                  >
                    <UserMinus className="w-4 h-4" />
                    <span>Remove from household</span>
                  </button>
                  {wouldOrphanOwner && (
                    <div className="px-3 py-2 text-[10px] text-[#8A93A6] bg-[#F5F3ED] border-t border-[#E2E0D8]">
                      A household must have at least one owner.
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </li>
  );
};

export default Household;
